/**
 * Links the operating system hands a running app.
 *
 * A web app never sees these: a link opens a tab, and the address bar is the
 * router. In a shell the app is already open, so the system delivers the URL as
 * an event and the app has to route it itself — which is what makes a shared
 * note open *in* markbird rather than in a browser next to it (App Store
 * guideline 4.2 counts this as native behaviour, and it is also simply right).
 *
 * Accepted: markbird's own web addresses, and a `markbird://` scheme for the
 * shells to register. Anything else is ignored rather than routed, because a URL
 * from the system is untrusted input.
 */

const APP_HOSTS = new Set(['app.markbird.md', 'markbird.md', 'localhost']);
/** Paths the app knows how to open from outside. */
const ROUTES = [/^\/s\/[\w-]+$/, /^\/p\/[a-z0-9-]+$/, /^\/$/];

/** The in-app path a system URL should open, or null if it is not ours. */
export function pathFromUrl(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  // markbird://s/<token> — the host is the first path segment in a custom scheme.
  const path =
    url.protocol === 'markbird:'
      ? `/${[url.hostname, url.pathname.replace(/^\/+/, '')].filter(Boolean).join('/')}`
      : url.pathname;

  if (url.protocol !== 'markbird:') {
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (!APP_HOSTS.has(url.hostname)) return null;
  }

  const clean = path.replace(/\/+$/, '') || '/';
  if (!ROUTES.some((route) => route.test(clean))) return null;
  return `${clean}${url.search}`;
}
